import React from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../components/Icon";

const METHOD_COLORS = {
    GET: "bg-green-100 text-green-700 border-green-200",
    POST: "bg-blue-100 text-blue-700 border-blue-200",
    PUT: "bg-amber-100 text-amber-700 border-amber-200",
    PATCH: "bg-orange-100 text-orange-700 border-orange-200",
    DELETE: "bg-red-100 text-red-700 border-red-200",
    QUERY: "bg-green-100 text-green-700 border-green-200",
    MUTATION: "bg-purple-100 text-purple-700 border-purple-200",
    SUBSCRIPTION: "bg-cyan-100 text-cyan-700 border-cyan-200",
};

const MAX_VISIBLE_PER_GROUP = 5;

export default function DiscoveredEndpoints({ endpoints, revealedCount }) {
    const { t } = useTranslation();
    const visible = endpoints.slice(0, revealedCount);
    const [expandedGroups, setExpandedGroups] = React.useState({});

    // Group by resource
    const grouped = {};
    visible.forEach((ep) => {
        if (!grouped[ep.resource]) grouped[ep.resource] = [];
        grouped[ep.resource].push(ep);
    });

    // Method summary counts across all visible
    const methodCounts = {};
    visible.forEach((ep) => { methodCounts[ep.method] = (methodCounts[ep.method] || 0) + 1; });

    const toggleGroup = (resource) => {
        setExpandedGroups((prev) => ({ ...prev, [resource]: !prev[resource] }));
    };

    return (
        <div className="space-y-3">
            {/* Summary bar */}
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-700">{t("builder.endpoints.discovered")}</h3>
                <div className="flex items-center gap-2">
                    {Object.entries(methodCounts).map(([method, count]) => (
                        <span key={method} className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${METHOD_COLORS[method] || "bg-gray-100 text-gray-600"}`}>
                            {count} {method}
                        </span>
                    ))}
                    <span className="text-xs text-gray-400 ml-1">{visible.length} {t("common.total")}</span>
                </div>
            </div>

            {/* Resource groups — collapsed by default when large */}
            {Object.entries(grouped).map(([resource, eps]) => {
                const isLarge = eps.length > MAX_VISIBLE_PER_GROUP;
                const isExpanded = expandedGroups[resource];
                const shownEps = isLarge && !isExpanded ? eps.slice(0, MAX_VISIBLE_PER_GROUP) : eps;
                const hiddenCount = eps.length - MAX_VISIBLE_PER_GROUP;
                const lastRevealedIdx = revealedCount - 1;

                return (
                    <div key={resource} className="space-y-1">
                        <button onClick={() => toggleGroup(resource)} className="flex items-center gap-1.5 w-full text-left group">
                            <Icon name={isExpanded || !isLarge ? "ChevronDown" : "ChevronRight"} className="w-3 h-3 text-gray-400 group-hover:text-gray-600" />
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{resource}</span>
                            <span className="text-[10px] text-gray-400">({eps.length})</span>
                        </button>
                        <div className="space-y-1 ml-4">
                            {shownEps.map((ep) => {
                                const globalIdx = endpoints.indexOf(ep);
                                const isNew = globalIdx === lastRevealedIdx;
                                return (
                                    <div
                                        key={ep.id}
                                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border bg-white text-sm ${isNew ? "animate-in fade-in slide-in-from-right-4 duration-500 ring-2 ring-blue-200" : ""}`}
                                    >
                                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${METHOD_COLORS[ep.method] || "bg-gray-100 text-gray-600"}`}>
                                            {ep.method}
                                        </span>
                                        <span className="font-mono text-xs text-gray-700 flex-1 truncate">{ep.path}</span>
                                        <span className="text-xs text-gray-400 hidden sm:inline truncate max-w-[140px]">{ep.description}</span>
                                    </div>
                                );
                            })}
                            {isLarge && !isExpanded && (
                                <button onClick={() => toggleGroup(resource)} className="text-xs text-blue-600 hover:text-blue-800 px-3 py-1">
                                    + {t("builder.endpoints.more", { count: hiddenCount })}
                                </button>
                            )}
                        </div>
                    </div>
                );
            })}

            {revealedCount < endpoints.length && (
                <div className="flex items-center gap-2 text-blue-500 text-xs animate-pulse">
                    <Icon name="Loader" className="w-3 h-3 animate-spin" />
                    <span>{t("builder.endpoints.discovering")}</span>
                </div>
            )}
        </div>
    );
}
